import { useCallback, useState } from "react";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { DELETE_TASK_MUTATION, type DeleteTaskResponse } from "./mutations";
import { TASKS_QUERY } from "../queries";
import type { CRMTask } from "@/types";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData, type TaskAnalyticsSource } from "../_utils/amplitude";

type UseDeleteTaskOptions = {
  task?: CRMTask;
  source?: TaskAnalyticsSource;
};

export default function useDeleteTask(options: UseDeleteTaskOptions = {}) {
  const [error, setError] = useState<string | null>(null);
  const { trackEvent } = useAmplitude();

  const [deleteTaskMutation, { loading }] = useMutation<DeleteTaskResponse>(DELETE_TASK_MUTATION, {
    refetchQueries: [TASKS_QUERY],
    awaitRefetchQueries: true
  });

  async function deleteTask(taskId: string) {
    setError(null);

    try {
      const result = await deleteTaskMutation({ variables: { taskId } });

      if (result.data?.deleteTask?.success) {
        if (options.source) {
          trackEvent(TASK_ANALYTICS_EVENTS.deleted, {
            ...(options.task ? getTaskAnalyticsData(options.task, options.source) : { taskId, source: options.source })
          });
        }
        showSnackbar("Task deleted");
        return true;
      }

      setError(result.data?.deleteTask?.message || "Failed to delete task");
      return false;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete task. Please try again.";
      setError(message);
      return false;
    }
  }

  const clearError = useCallback(() => setError(null), []);

  return { deleteTask, loading, error, clearError };
}
